export const UPPER_RIGHT = ["18","17","16","15","14","13","12","11"];
export const UPPER_LEFT  = ["21","22","23","24","25","26","27","28"];
export const LOWER_LEFT  = ["38","37","36","35","34","33","32","31"];
export const LOWER_RIGHT = ["41","42","43","44","45","46","47","48"];

export const isAnterior = (n) => ["11","12","13","21","22","23","31","32","33","41","42","43"].includes(n);
export const isIncisor  = (n) => ["11","12","21","22","31","32","41","42"].includes(n);
export const isCanine   = (n) => ["13","23","33","43"].includes(n);
export const isPremolar = (n) => ["14","15","24","25","34","35","44","45"].includes(n);
export const isMolar    = (n) => ["16","17","18","26","27","28","36","37","38","46","47","48"].includes(n);
export const isUpper    = (n) => n.startsWith("1") || n.startsWith("2");

export const POSTERIOR_SURFACES = ["M", "O", "D", "B", "L", "C"];
export const ANTERIOR_SURFACES  = ["M", "I", "D", "B", "L", "C"];

export const STATUS_OPTS = [
  { v: "healthy",    label: "Shëndoshë" },
  { v: "decayed",    label: "Kariesi" },
  { v: "treated",    label: "I trajtuar" },
  { v: "planned",    label: "I planifikuar" },
  { v: "missing",    label: "Mungon" },
  { v: "crown",      label: "Kurorë" },
  { v: "root_canal", label: "Kanal" },
  { v: "implant",    label: "Implant" },
];

// Editor shows only base statuses — root/crown handled as separate layers
export const BASE_STATUS_OPTS = STATUS_OPTS.filter((o) =>
  ["healthy", "decayed", "treated", "planned", "missing"].includes(o.v)
);

export const ROOT_OPTS = [
  { v: "normal",     label: "Normale" },
  { v: "kanal",      label: "Kanal" },
  { v: "kanal_vide", label: "Kanal + Vidë" },
  { v: "implant",    label: "Implant" },
];

export const CROWN_OPTS = [
  { v: "natyrale", label: "Natyrale" },
  { v: "kurore",   label: "Kurorë" },
  { v: "fasete",   label: "Fasetë" },
];

// Materialet e kurorave — pamje + etiketa
export const CROWN_MATERIALS = [
  { v: "metal_porcelan", label: "Metal-Porcelan", short: "MP",   badge: "#6B7280", bar: "#98A2AE" },
  { v: "zirkon",         label: "Zirkon",         short: "Zr",   badge: "#0E7490", bar: "#EDF1F4" },
  { v: "zirkon_emax",    label: "Zirkon E-max",   short: "Emax", badge: "#7C3AED", bar: "#DCE9F0" },
];
export const MATERIAL_LABEL = Object.fromEntries(CROWN_MATERIALS.map((m) => [m.v, m.label]));
export const MATERIAL_SHORT = Object.fromEntries(CROWN_MATERIALS.map((m) => [m.v, m.short]));
export const MATERIAL_BAR   = Object.fromEntries(CROWN_MATERIALS.map((m) => [m.v, m.bar]));

// Efektet vizuale të materialit mbi sprite-in real të dhëmbit (SVG filter + canvas filter)
export const MAT_FX = {
  metal_porcelan: { sat: 0.10, slope: 1.18, intercept: 0.06, canvas: "saturate(0.10) brightness(1.28)" },
  zirkon:         { sat: 0.04, slope: 1.28, intercept: 0.10, canvas: "saturate(0.04) brightness(1.45)" },
  zirkon_emax:    { sat: 0.55, slope: 1.10, intercept: 0.02, canvas: "saturate(0.55) brightness(1.12)" },
};

// Konektorët e urës (pamje qeramike, jo shirit i thjeshtë)
export const CONNECTOR_FX = {
  metal_porcelan: { fill: "#EEF1F3", stroke: "#8C97A3" },
  zirkon:         { fill: "#F7FAFB", stroke: "#AEB9C2" },
  zirkon_emax:    { fill: "#F6F0E4", stroke: "#C4B491" },
  "":             { fill: "#EAD9AF", stroke: "#C9963A" },
};

// Layered tooth state with legacy single-status fallback
export function deriveLayers(record) {
  const s = record?.status;
  return {
    rootStatus: record?.root_status || (s === "root_canal" ? "kanal" : s === "implant" ? "implant" : "normal"),
    crownStatus: record?.crown_status || (s === "crown" ? "kurore" : "natyrale"),
  };
}

export const CATEGORY_COLOR = {
  "Mbushje":          "#3B6FA8",
  "Endodonti":        "#C04848",
  "Protetikë":        "#C9963A",
  "Implantologji":    "#5A5A5A",
  "Kirurgji":         "#000000",
  "Periodontologji":  "#A05A3B",
  "Ortodonci":        "#7B5EA8",
  "Higjenë":          "#4A9A6B",
  "Diagnostikë":      "#888888",
  "default":          "#3B6FA8",
};

// Photorealistic sprite metadata: width/height aspect (after alpha trim)
export const SPRITE_ASPECT = {
  "incisor-upper": 0.33,
  "incisor-lower": 0.2975,
  "canine": 0.3025,
  "premolar": 0.37,
  "molar-upper": 0.6,
  "molar-lower": 0.53,
  "implant": 0.365,
};

export const spriteKeyFor = (n) =>
  isMolar(n) ? (isUpper(n) ? "molar-upper" : "molar-lower")
  : isPremolar(n) ? "premolar"
  : isCanine(n) ? "canine"
  : (isUpper(n) ? "incisor-upper" : "incisor-lower");

// Root canal paths per sprite (image-relative fractions: from=orifice, bend=curve, to=apex)
export const ROOT_CANALS = {
  "molar-upper": [
    { from: [0.36, 0.46], bend: [0.16, 0.66], to: [0.11, 0.86] },
    { from: [0.50, 0.47], bend: [0.51, 0.70], to: [0.52, 0.92] },
    { from: [0.64, 0.46], bend: [0.76, 0.66], to: [0.78, 0.84] },
  ],
  "molar-lower": [
    { from: [0.38, 0.46], bend: [0.23, 0.66], to: [0.18, 0.88] },
    { from: [0.62, 0.46], bend: [0.68, 0.66], to: [0.66, 0.85] },
  ],
  "premolar":      [{ from: [0.50, 0.45], bend: [0.52, 0.70], to: [0.54, 0.92] }],
  "canine":        [{ from: [0.50, 0.44], bend: [0.50, 0.70], to: [0.50, 0.93] }],
  "incisor-upper": [{ from: [0.50, 0.44], bend: [0.51, 0.68], to: [0.52, 0.90] }],
  "incisor-lower": [{ from: [0.50, 0.44], bend: [0.51, 0.68], to: [0.52, 0.92] }],
};

export function categorizeByName(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("mbushje") || n.includes("composite") || n.includes("kompozit")) return "Mbushje";
  if (n.includes("kanal") || n.includes("endo")) return "Endodonti";
  if (n.includes("kuror") || n.includes("crown") || n.includes("protet")) return "Protetikë";
  if (n.includes("implant")) return "Implantologji";
  if (n.includes("heqje") || n.includes("kirurg")) return "Kirurgji";
  if (n.includes("ortodonc") || n.includes("aparat")) return "Ortodonci";
  if (n.includes("pastrim") || n.includes("higjen")) return "Higjenë";
  if (n.includes("rëntgen") || n.includes("rentgen") || n.includes("vizitë")) return "Diagnostikë";
  if (n.includes("periodont")) return "Periodontologji";
  return "Mbushje";
}
